// change.ts
var nothingHere = Symbol("nothing here");
var isPrimitive = (value) => typeof value !== "object" && typeof value !== "function" || value === null || value instanceof Date;
var Change = class _Change {
  // with the blockWriteback you can signal the attaching code that these are updates, not actual changes
  // which would need to be processed back to its origin (mongo documents coming from the server for example)
  constructor(value, path) {
    this.path = [];
    if (path.some((part) => part === void 0))
      throw new Error("cant have undefined in path: " + path);
    this.path = path;
    this.value = value;
  }
  static resolvePath(context, path) {
    let current = context;
    for (const pathPart of path) {
      try {
        if (!current || !(pathPart in current))
          return nothingHere;
      } catch (error) {
        return nothingHere;
      }
      current = current[pathPart];
    }
    return current;
  }
  // TODO currently assumes that old has all the keys in question
  // return value null makes the next higher instance reset the whole branch
  // use [] to signal no changes
  static getDiff(old, newObj) {
    if (Array.isArray(old) || Array.isArray(newObj)) {
      if (Array.isArray(old) && !Array.isArray(newObj) || !Array.isArray(old) && Array.isArray(newObj))
        return [new _Change(newObj, [])];
      if (old.length === newObj.length) {
        return old.map((oldEntry, i) => {
          const changeResult = _Change.getDiff(oldEntry, newObj[i]);
          const changes = changeResult || [new _Change(newObj[i], [])];
          changes.forEach((change) => change.expandPath([i]));
          return changes;
        }).reduce((flat, curr) => flat.concat(curr), []);
      } else
        return null;
    }
    if (old === void 0 && newObj === void 0)
      return [];
    if (old === null && newObj === null)
      return [];
    if (old === void 0 || old === null)
      return [new _Change(newObj, [])];
    if (newObj === void 0 || newObj === null)
      return [new _Change(void 0, [])];
    if (isPrimitive(old)) {
      if (!isPrimitive(newObj) || typeof old !== typeof newObj)
        return [new _Change(newObj, [])];
      return old?.valueOf() === newObj?.valueOf() ? [] : [new _Change(newObj, [])];
    }
    if (isPrimitive(newObj)) {
      if (!isPrimitive(old) || typeof old !== typeof newObj)
        return [new _Change(newObj, [])];
      return old?.valueOf() === newObj?.valueOf() ? [] : [new _Change(newObj, [])];
    }
    const combinedKeys = /* @__PURE__ */ new Set([...Object.keys(old), ...Object.keys(newObj)]);
    const changesArray = [...combinedKeys].map((key) => {
      const changeResult = _Change.getDiff(old[key], newObj[key]);
      const changes = changeResult || [new _Change(newObj[key], [])];
      changes.forEach((change) => change.expandPath([key]));
      return changes;
    }).reduce((flat, curr) => flat.concat(curr), []);
    return changesArray;
  }
  /**
   * starts from {} and applies all other objects deeply. Like {...oldObj} but equally named keys won't overwrite.
   * @returns new combined object
   */
  static deepMerge(...objects) {
    const newObj = {};
    for (const obj of objects) {
      _Change.getDiff(newObj, obj).filter((change) => change.getValue() !== void 0).forEach((change) => change.applyToContext(newObj, true));
    }
    return newObj;
  }
  /**
   * returns true if there has been a change
   *
   * @param context
   */
  applyToContext(context, createNecessaryStructure = false) {
    if (this.path.length === 0)
      throw new Error(
        "you gave me an empty path and didnt inset it later either.I cant apply to the context now because I would overwrite my reference"
      );
    let targetObject;
    let resolvedVal;
    try {
      resolvedVal = _Change.resolvePath(context, this.path);
    } catch (e) {
      throw new Error(
        `cant resolve ${this.path.join(".")} on context, maybe we're colliding with an explicitly set undefined?`
      );
    }
    if (resolvedVal !== nothingHere) {
      targetObject = _Change.resolvePath(context, this.path.slice(0, -1));
    } else {
      if (!createNecessaryStructure) {
        console.warn(context);
        throw new Error(
          "trying to apply to a non-existent path without allowing creation of necessary structure: " + JSON.stringify(this.path)
        );
      }
      let current = context;
      this.path.slice(0, -1).forEach((pathPart, index) => {
        if (!(pathPart in current)) {
          const nextPathPart = this.path[index + 1];
          if (!isNaN(+nextPathPart)) {
            current[pathPart] = [];
          } else {
            current[pathPart] = {};
          }
        }
        current = current[pathPart];
      });
      targetObject = current;
    }
    const lastPathPart = this.path.slice(-1)[0];
    if (targetObject[lastPathPart] === this.value)
      return false;
    targetObject[lastPathPart] = this.value;
    return true;
  }
  expandPath(additionalPath) {
    this.path = additionalPath.concat(this.path);
    return this;
  }
  contractPath(by) {
    this.path.splice(0, by);
    return this;
  }
  concernsPath(path) {
    let localPathLocation = 0;
    for (let pathPart of this.getPath()) {
      if (pathPart === path[localPathLocation])
        localPathLocation++;
      else
        return false;
      if (localPathLocation === path.length)
        return true;
    }
    if (isPrimitive(this.getValue()))
      return false;
    let currValue = this.getValue();
    const remainingPath = path.slice(localPathLocation);
    for (let pathPart of remainingPath) {
      if (!(pathPart in currValue)) {
        localPathLocation++;
        return false;
      }
    }
    return localPathLocation === path.length - 1;
  }
  getValue() {
    return this.value;
  }
  getPath() {
    return this.path;
  }
  clone() {
    return new _Change(this.value, this.path);
  }
};
var SpliceChange = class _SpliceChange extends Change {
  constructor(newEntry, indexToSplice, removeNumber, path) {
    if (!path)
      throw new Error();
    super(newEntry, [...path, indexToSplice]);
    this.removeNumber = removeNumber;
  }
  applyToContext(context) {
    const current = Change.resolvePath(context, this.path.slice(0, -1));
    if (this.value !== null) {
      current.splice(this.path.slice(-1)[0], this.removeNumber, this.value);
    } else
      current.splice(this.path.slice(-1)[0], this.removeNumber);
    return true;
  }
  clone() {
    return new _SpliceChange(this.value, this.path.slice(-1)[0], this.removeNumber, this.path.slice(0, -1));
  }
};
export {
  Change,
  SpliceChange,
  nothingHere
};
